import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import {
  Check,
  Download,
  ExternalLink,
  FileText,
  Link2,
  Loader2,
  Lock,
  Mail,
  MapPin,
  Phone,
  Tags,
  Trophy,
  Users,
} from "lucide-react";

import { Header } from "../../../components/Header/Header";
import ConfirmDialog from "../../../components/ConfirmDialog/ConfirmDialog";
import {
  deadlineUrgency,
  downloadTenderDocument,
  fetchTender,
  formatDaysRemaining,
  formatDeadline,
  importTender,
} from "../../../services/tendersApi";
import {
  Callout,
  DESCRIPTION_PROSE,
  DescriptionText,
  ImportedPill,
  NoticePill,
  SetAsidePill,
  SourceBadge,
  URGENCY_TEXT,
  withRules,
} from "./TenderUI";
import TenderFitCard from "./TenderFitCard";

// Collapsed height for a long description. A pixel clamp, not a character
// count: the text arrives as the agency's own HTML, and slicing that mid-tag
// produces broken markup rather than a preview.
const DESCRIPTION_CLAMP_PX = 320;


//: Links that point straight at a file. Worth saying so: it is the difference
//: between "a page to read" and "the solicitation you were looking for".
const LINK_FILE_TYPES = ["pdf", "doc", "docx", "xls", "xlsx", "csv", "ppt", "pptx", "zip", "txt"];

// "48828 KB" is not a file size anyone reads. Scale the unit instead.
const formatBytes = (bytes) => {
  if (!bytes || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
};

// Copy for the pre-import warnings. Pure so the cases can be read in one place
// and none of them can accidentally block: every one keeps a confirm button.
const warningDialog = (kind, tender, attachments, links) => {
  const source = tender?.source_label || "SAM.gov";
  const openSource = tender?.source_url
    ? { label: `Open on ${source}`, href: tender.source_url }
    : null;
  // Both warnings can be true at once. Rather than show a second modal, the
  // duplicate becomes one more sentence in the one that matters more.
  const alsoImported = tender?.is_imported
    ? " Someone in your workspace has already imported this tender, so this creates a second, separate project."
    : "";

  if (kind === "reimport") {
    return {
      title: "Already in a project",
      message:
        "Someone in your workspace has already imported this tender. Importing again creates a second, separate project.",
      confirmLabel: "Import again",
    };
  }

  if (kind === "links") {
    const n = links.length;
    return {
      title: n === 1 ? "Only a link on this tender" : "Only links on this tender",
      message:
        `The agency published ${n} web link${n === 1 ? "" : "s"} and no files. ` +
        `Links open on ${source} and aren't brought into the project, so it will ` +
        `start without documents.` + alsoImported,
      tip: "Download the package from the link, then upload it to the project.",
      confirmLabel: "Create project anyway",
      secondaryAction: openSource,
    };
  }

  if (kind === "restricted") {
    const n = attachments.length;
    // A tender can carry restricted files and links at once. The restriction is
    // the reason nothing arrives, but saying only that leaves the user
    // wondering what happened to the links they can see on the page.
    const andLinks = links.length
      ? ` Its ${links.length} web link${links.length === 1 ? "" : "s"} stay on ${source} and aren't brought in either.`
      : "";
    return {
      title: "Every attachment is restricted",
      message:
        `${n === 1 ? "The one attachment" : `All ${n} attachments`} on this tender ` +
        `${n === 1 ? "is" : "are"} restricted by the agency, so none can be ` +
        `downloaded. The project will start with the tender details only.` +
        andLinks + alsoImported,
      tip: `Download them from ${source} with your login, then upload them to the project.`,
      confirmLabel: "Create project anyway",
      secondaryAction: openSource,
    };
  }

  return {
    title: "No documents on this tender",
    message:
      "The agency didn't attach any files, so the project will start with the " +
      "tender details only. You can upload the solicitation documents once you're in." +
      alsoImported,
    confirmLabel: "Create project anyway",
    secondaryAction: openSource
      ? { label: `Check the notice on ${source}`, href: tender.source_url }
      : null,
  };
};

const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url || "";
  }
};

const linkFileType = (url) => {
  const match = /\.([a-z0-9]{2,5})(?:$|[?#])/i.exec(url || "");
  const ext = match?.[1]?.toLowerCase();
  return LINK_FILE_TYPES.includes(ext) ? ext : "";
};

// Kill switch: flip to false to disable importing without touching the flow
// behind the button (API, downloads, progress rows).
const IMPORT_ENABLED = true;

const formatMoney = (amount) => {
  const value = Number(amount);
  if (!amount || Number.isNaN(value)) return amount || "";
  return value.toLocaleString("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  });
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
};

// "SEIDLE, LINDA" → "SL"; "Main Person" → "MP". Two letters, like the app's
// uploaded-by avatars.
const initials = (name) => {
  const parts = String(name || "").split(/[\s,]+/).filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0].toUpperCase()).join("") || "?";
};

function ContactPerson({ contact }) {
  return (
    <div className="flex items-start gap-3 min-w-0">
      <div className="w-9 h-9 rounded-full bg-[#C95EBE] text-white font-outfit text-[13px] font-medium
                      flex items-center justify-center shrink-0 mt-0.5">
        {initials(contact.name || contact.email)}
      </div>
      <div className="min-w-0">
        <div className="font-outfit text-[11px] uppercase tracking-wide text-[#9CA3AF] font-semibold">
          {contact.role === "primary" ? "Primary contact" : "Alternative contact"}
        </div>
        <div className="font-outfit text-[14px] font-semibold text-[#18101C] truncate">
          {contact.name || contact.email}
        </div>
        {contact.title && (
          <div className="font-outfit text-[12px] text-[#71717A]">{contact.title}</div>
        )}
        <div className="flex flex-col gap-1 mt-1.5">
          {contact.email && (
            <a
              href={`mailto:${contact.email}`}
              className="inline-flex items-center gap-1.5 font-outfit text-[13px] font-medium
                         text-[#BB42AE] hover:underline break-all"
            >
              <Mail className="w-3.5 h-3.5 shrink-0" />
              {contact.email}
            </a>
          )}
          {contact.phone && (
            <span className="inline-flex items-center gap-1.5 font-outfit text-[13px] text-[#52525B]">
              <Phone className="w-3.5 h-3.5 shrink-0 text-[#9CA3AF]" />
              {contact.phone}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// The codes of one scheme, in the order the agency published them.
const codesOf = (tender, scheme) =>
  (tender.classifications || []).filter((c) => c.scheme === scheme);

// Codes read "3416" on their own. SAM.gov sends no name for one, so the name
// shown underneath comes from the government's own NAICS and PSC manuals
// instead, resolved by the exact code at sync time.
const codeValue = (rows) => rows.map((c) => c.code).join(", ");
const codeDescription = (rows) =>
  rows.map((c) => c.label || c.group).filter(Boolean).join(", ");

// An empty set-aside is not missing data. "No set aside used" and its variants
// are normalised away on ingest, so nothing here means the work is unrestricted.
const setAsideValue = (tender) =>
  tender.set_aside_label || tender.set_aside_code || "Open to all bidders";

// Seven federal tenders in ten name no place of performance at all. The country
// is only worth showing when it is not the United States.
const placeValue = (tender) => {
  const country = tender.place_country;
  const foreign = country && !["US", "USA"].includes(country.toUpperCase());
  return [tender.place, foreign ? country : ""].filter(Boolean).join(", ");
};

function Meta({ label, value, sub, missing }) {
  return (
    <div className="font-outfit">
      <dt className="text-[11px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-1">
        {label}
      </dt>
      <dd
        className={`text-[14px] leading-snug ${
          missing ? "text-[#9CA3AF] italic font-normal" : "text-[#18101C] font-medium"
        }`}
      >
        {value}
      </dd>
      {sub && <span className="block text-[12px] text-[#9CA3AF] mt-[3px]">{sub}</span>}
    </div>
  );
}

// Every section of the page is one of these: white card on the grey canvas,
// the same surface the rest of the app puts its content on.
function Card({ children, className = "" }) {
  return (
    <section className={`bg-white border border-[#E5E7EB] rounded-xl px-6 py-5 mb-4 ${className}`}>
      {children}
    </section>
  );
}

function CardHeading({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="mb-4 font-outfit">
      <div className="flex items-center justify-between gap-3">
        <h2 className="inline-flex items-center gap-2 text-[15px] font-semibold text-[#18101C]">
          {Icon && <Icon className="w-4 h-4 text-[#BB42AE]" />}
          {title}
        </h2>
        {action}
      </div>
      {subtitle && <p className="text-[13px] text-[#9CA3AF] mt-0.5">{subtitle}</p>}
    </div>
  );
}

// Page shell, shared by the loading, error and loaded states so the chrome does
// not jump about while the tender arrives.
function DetailShell({ isMenuOpen, onBack, children, footer, scrollKey }) {
  const scrollRef = React.useRef(null);
  // Navigating between versions of a contract swaps the route param but keeps
  // this inner scroll container where it was — parked at the bottom, on an
  // "Earlier versions" list that looks identical, which reads as "nothing
  // happened". Every tender change starts the reader at the top.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTo({ top: 0 });
  }, [scrollKey]);

  return (
    <div className="flex flex-col h-full w-full font-outfit overflow-hidden bg-[#FAFAFA]">
      <div className="flex-shrink-0 bg-white">
        <Header name="Tender Search" icon="/arrow-back.svg" isMenuOpen={isMenuOpen} onClick={onBack} />
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto custom-scrollbar px-6 py-5">
        <div className="max-w-[920px]">{children}</div>
      </div>

      {footer}
    </div>
  );
}

export default function TenderDetail() {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMenuOpen } = useOutletContext() || {};

  // The search this tender was opened from, so going back restores it rather
  // than dropping the user on an unfiltered list.
  const fromSearch = location.state?.from || "";

  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [expanded, setExpanded] = useState(false);

  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  // Which warning to show before importing, or null to go straight ahead.
  // One value rather than a flag per case, so two dialogs can never stack.
  const [pendingWarning, setPendingWarning] = useState(null);
  // Guards against a second click landing before `importing` re-renders.
  const importingRef = useRef(false);

  const loadedIdRef = useRef(null);

  const goBack = () => navigate({ pathname: "/tender-search", search: fromSearch });

  useEffect(() => {
    // Correcting the address to the current version changes tenderId, and
    // without this the page would fetch the same tender a second time and
    // flash its skeleton on the way.
    if (loadedIdRef.current === String(tenderId)) return undefined;

    let active = true;
    setLoading(true);
    setError(null);

    fetchTender(tenderId)
      .then((data) => {
        if (!active) return;
        loadedIdRef.current = String(data.id);
        setTender(data);
        // A contract is republished for every amendment, and the API answers
        // with the current version whichever notice was asked for. Correct the
        // address so a shared or bookmarked link stops pointing at an old one.
        // `replace`, never push: pushing would make Back return to the stale id
        // and redirect forward again, trapping the user.
        if (String(data.id) !== String(tenderId)) {
          navigate(`/tender-search/${data.id}`, {
            replace: true,
            state: { from: fromSearch },
          });
        }
        // Everything ticked by default: a user who wants all of it — the common
        // case — should not have to click each one. Restricted files are not
        // tickable at all: no automated path can fetch them, and links are not
        // here to tick, since there is no file behind one.
        setSelected(new Set(
          (data.attachments || []).filter((d) => !d.access_restricted).map((d) => d.id)
        ));
        // Opening a second tender must not inherit the first one's expanded
        // description.
        setExpanded(false);
      })
      .catch((err) => {
        if (!active) return;
        setError(
          err?.response?.status === 404
            ? "This tender is no longer available."
            : "Could not load this tender. Please try again."
        );
      })
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [tenderId, fromSearch, navigate]);

  // Two lists, as SAM.gov publishes them. An attachment is a file we can fetch;
  // a link is the agency's own page and stays on their site.
  const attachments = useMemo(() => tender?.attachments || [], [tender]);
  const links = useMemo(() => tender?.links || [], [tender]);

  const toggleDoc = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Per-document, not a single page-wide flag: downloading one attachment
  // must not freeze the Select all / import controls or the other rows.
  const [downloadingIds, setDownloadingIds] = useState(() => new Set());

  const handleDownloadDoc = async (e, doc) => {
    e.stopPropagation();
    if (downloadingIds.has(doc.id)) return;
    setDownloadingIds((prev) => new Set(prev).add(doc.id));
    try {
      await downloadTenderDocument(tenderId, doc.id, doc.name);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(doc.id);
        return next;
      });
    }
  };

  // Only attachments are selectable. There is no file behind a link, so
  // offering a tick box for one promises something import cannot deliver.
  const selectable = useMemo(
    () => attachments.filter((d) => !d.access_restricted),
    [attachments]
  );
  const allSelected = selectable.length > 0 && selected.size === selectable.length;

  // The bulk feed cannot see attachments, so for most tenders the list is
  // unknown until import time — the job then fetches whatever SAM.gov lists.
  const unknownDocs =
    tender?.has_documents === null && attachments.length === 0 && links.length === 0;

  // The description arrives as the agency's HTML for about seven tenders in ten,
  // sanitised server-side. The rest is plain text and keeps its line breaks.
  const descriptionIsHtml = tender?.description_format === "html";

  // Close the paragraph the divider sat in and open a fresh one after it, or
  // the text that followed it ends up loose and unstyled. Only inert
  // punctuation is replaced, so nothing reaches the markup that the server-side
  // sanitiser did not already pass.
  const descriptionHtml = useMemo(() => {
    if (!descriptionIsHtml || !tender?.description) return "";
    return withRules(tender.description);
  }, [descriptionIsHtml, tender?.description]);

  const descriptionRef = useRef(null);
  const [descriptionOverflows, setDescriptionOverflows] = useState(false);

  // Measured rather than guessed: a short description with a tall table needs
  // the control, and a long one of short lines may not.
  useLayoutEffect(() => {
    const node = descriptionRef.current;
    if (!node) {
      setDescriptionOverflows(false);
      return;
    }
    setDescriptionOverflows(node.scrollHeight > DESCRIPTION_CLAMP_PX + 24);
  }, [tender?.description, tender?.description_format]);

  const startImport = async () => {
    // `importing` is state, so it does not disable the button until the next
    // render — two fast clicks both got through and created two projects. A
    // ref blocks the second one synchronously.
    if (importingRef.current) return;
    importingRef.current = true;

    setPendingWarning(null);
    setImporting(true);
    setImportError(null);
    try {
      const result = await importTender(tender.id, {
        documentIds: unknownDocs ? undefined : [...selected],
      });
      toast.success("Project created. Documents are downloading in the background");
      const orgId = Cookies.get("selected_org_id");
      // tenderImportId lets the project page poll the job and show files as
      // they land, instead of an empty list until a manual refresh. The URL
      // must include /project-files directly: the bare project route redirects
      // there via <Navigate replace>, and that redirect drops location.state.
      navigate(
        orgId
          ? `/rfp-projects/project/${result.folder_id}/org/${orgId}/project-files`
          : `/rfp-projects/project/${result.folder_id}/project-files`,
        { state: { tenderImportId: result.id } }
      );
    } catch (err) {
      setImportError(
        err?.response?.data?.error || "Could not start the import. Please try again."
      );
      setImporting(false);
      // Only release on failure: a success navigates away, and letting go here
      // would reopen the double-click window during that transition.
      importingRef.current = false;
    }
  };

  // Roughly two tenders in five bring in no files at all. Saying so before the
  // click is the difference between a considered decision and a project that
  // silently cannot do anything. Every one of these warns and then lets the
  // user through — none of them blocks.
  const importWarning = () => {
    // Unknown is not empty. The import discovers the list, so there is nothing
    // truthful to warn about yet.
    if (unknownDocs) return null;
    // Something is fetchable, so the project will have documents.
    if (selectable.length > 0) return null;
    if (attachments.length > 0) return "restricted";
    if (links.length > 0) return "links";
    return "none";
  };

  const requestImport = () => {
    // A no-documents warning outranks the duplicate one: both can be true, and
    // an empty project is the more consequential surprise. The duplicate is
    // folded into that dialog's wording rather than shown as a second modal.
    // The corpus warns on duplicates rather than blocking them — a colleague
    // may already be bidding this, and that is worth a pause, not a wall.
    const warning = importWarning();
    if (warning) setPendingWarning(warning);
    else if (tender?.is_imported) setPendingWarning("reimport");
    else startImport();
  };

  if (loading) {
    return (
      <DetailShell isMenuOpen={isMenuOpen} onBack={goBack} scrollKey={tenderId}>
        <Card>
          <div className="flex gap-2 mb-4">
            <div className="h-[22px] w-24 rounded-full bg-[#F4F4F5] animate-pulse" />
            <div className="h-[22px] w-32 rounded-full bg-[#F4F4F5] animate-pulse" />
          </div>
          <div className="h-7 w-3/4 rounded bg-[#F4F4F5] animate-pulse mb-3" />
          <div className="h-4 w-1/2 rounded bg-[#F4F4F5] animate-pulse" />
        </Card>
        <Card>
          <div className="h-4 w-28 rounded bg-[#F4F4F5] animate-pulse mb-4" />
          <div className="h-24 rounded bg-[#F4F4F5] animate-pulse" />
        </Card>
      </DetailShell>
    );
  }

  if (error) {
    return (
      <DetailShell isMenuOpen={isMenuOpen} onBack={goBack} scrollKey={tenderId}>
        <Callout tone="danger">{error}</Callout>
      </DetailShell>
    );
  }

  const urgency = deadlineUrgency(tender);

  // Nothing to send only when documents are known, some exist, and the user
  // unticked every fetchable one. Unknown lists stay enabled: the import job
  // discovers and fetches whatever SAM.gov lists.
  const nothingSelected = !unknownDocs && selectable.length > 0 && selected.size === 0;

  const footer = (
    <div className="flex-shrink-0 bg-white border-t border-black/10 px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
      <div className="font-outfit text-[13px] text-[#71717A]">
        {selectable.length > 0 ? (
          <>
            <b className="text-[#18101C] font-semibold">{selected.size}</b> of{" "}
            {selectable.length} attachment{selectable.length === 1 ? "" : "s"} selected
            <span className="block text-[12px] text-[#9CA3AF] mt-0.5">
              You'll land in the project right away. Files finish in the background
            </span>
          </>
        ) : attachments.length > 0 ? (
          // Attachments exist but every one is locked. Saying "no documents"
          // here would be wrong: the agency published them, we just cannot
          // reach them without a login.
          <>
            Nothing here can be fetched automatically
            <span className="block text-[12px] text-[#9CA3AF] mt-0.5">
              {attachments.length === 1
                ? "The one attachment is"
                : `All ${attachments.length} attachments are`}{" "}
              restricted by the agency, so you'll need to download
              {attachments.length === 1 ? " it" : " them"} from {tender.source_label} yourself
            </span>
          </>
        ) : links.length > 0 ? (
          <>
            No files to download on this tender
            <span className="block text-[12px] text-[#9CA3AF] mt-0.5">
              The agency published {links.length} link{links.length === 1 ? "" : "s"} instead.
              They stay on {tender.source_label}, so you'll need to add documents yourself
            </span>
          </>
        ) : unknownDocs ? (
          <>
            Documents will be fetched from {tender.source_label} during import
            <span className="block text-[12px] text-[#9CA3AF] mt-0.5">
              We couldn't reach {tender.source_label} to list them just now, so the
              import checks again
            </span>
          </>
        ) : (
          "The agency attached no documents; the project is created with the tender details"
        )}
      </div>

      <button
        type="button"
        onClick={requestImport}
        disabled={!IMPORT_ENABLED || importing || nothingSelected}
        title={
          !IMPORT_ENABLED
            ? "Importing is temporarily disabled"
            : nothingSelected
            ? "Select at least one document"
            : undefined
        }
        className="flex font-outfit items-center gap-2 text-[14px] px-3 py-[6px] rounded-md font-medium
                   bg-[#A04A97] border border-[#A04A97] text-white transition-opacity
                   hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {importing ? (
          <>
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Creating project...
          </>
        ) : (
          <>
            <Download className="w-4 h-4" />
            Create Project
          </>
        )}
      </button>
    </div>
  );

  return (
    <DetailShell isMenuOpen={isMenuOpen} onBack={goBack} scrollKey={tenderId} footer={footer}>

      {/* Overview: everything needed to decide "is this worth reading further"
          in one block — pills, title, agency, then the facts grid. */}
      <Card>
        <div className="flex gap-[7px] flex-wrap items-center mb-3">
          <SourceBadge label={tender.source_label} />
          <NoticePill type={tender.notice_type} label={tender.notice_type_label} />
          <SetAsidePill label={tender.set_aside_label} />
          {tender.is_imported && <ImportedPill />}
        </div>

        <h1 className="font-outfit text-[22px] font-semibold text-[#18101C] leading-[1.3] mb-2">
          {tender.title}
        </h1>

        <p className="font-outfit text-[14px] text-[#71717A]">
          {tender.buyer_path || tender.buyer}
          {tender.source_url && (
            <>
              {" · "}
              <a
                href={tender.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[#BB42AE] font-medium no-underline hover:underline"
              >
                View on {tender.source_label}
                <ExternalLink className="w-3 h-3" />
              </a>
            </>
          )}
        </p>

        <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-5 mt-5 pt-5 border-t border-[#F1F1F3]">
          <Meta
            label="Closes"
            missing={!tender.closes_at}
            value={
              <span className={`font-semibold ${URGENCY_TEXT[urgency]}`}>
                {formatDaysRemaining(tender) || "Not published"}
              </span>
            }
            // formatDeadline shows the date alone when the source published no
            // timezone — a quarter of federal tenders. A time we cannot vouch
            // for is worse than no time at all.
            sub={formatDeadline(tender)}
          />
          <Meta
            label="Solicitation no."
            missing={!tender.reference}
            value={tender.reference || "Not published"}
          />
          <Meta
            label="Posted"
            missing={!tender.published_at}
            value={
              tender.published_at
                ? new Date(tender.published_at).toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })
                : "Not published"
            }
          />
          {/* The listing's own removal date. For the 28% of tenders with no
              response deadline (open BAAs), this is the only real date anchor. */}
          {tender.archived_at && (
            <Meta
              label="Listed until"
              value={formatDate(tender.archived_at)}
              sub="Removed from SAM.gov after this date"
            />
          )}
          <Meta
            label="Source"
            value={tender.source_label}
            sub={
              tender.last_confirmed_at
                ? `Confirmed ${new Date(tender.last_confirmed_at).toLocaleDateString()}`
                : null
            }
          />
        </dl>
      </Card>

      {/* Directly under the overview, because it answers the question the
          overview raises. Anything further down is read after the decision has
          already been made, which is too late to save the reader anything. */}
      <TenderFitCard tender={tender} />

      {/* How the agency filed this contract: who may bid, what it is classified
          as, and where the work happens. Kept in its own card rather than mixed
          into the facts above, because bidders check eligibility and codes as
          one decision. */}
      <Card>
        <CardHeading
          icon={Tags}
          title="Classification"
          subtitle="How the agency has filed this contract"
        />
        <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-5">
          <Meta
            label="Set aside"
            value={setAsideValue(tender)}
            sub={
              tender.set_aside_label || tender.set_aside_code
                ? "Only qualifying businesses may bid"
                : "No restriction published by the agency"
            }
          />
          <Meta
            label="Product service code"
            missing={codesOf(tender, "psc").length === 0}
            value={codeValue(codesOf(tender, "psc")) || "Not listed by the agency"}
            sub={codeDescription(codesOf(tender, "psc")) || null}
          />
          <Meta
            label="NAICS code"
            missing={codesOf(tender, "naics").length === 0}
            value={codeValue(codesOf(tender, "naics")) || "Not listed by the agency"}
            sub={codeDescription(codesOf(tender, "naics")) || null}
          />
          {/* Our own grouping, which is what the Industry filter searches on.
              The agency publishes no such thing. */}
          <Meta
            label="Industry"
            missing={!tender.categories?.length}
            value={
              tender.categories?.length
                ? tender.categories.map((c) => c.label).join(", ")
                : "Could not be placed from the codes"
            }
            sub={tender.categories?.length ? "DocuSensa category" : null}
          />
          <Meta
            label="Place of performance"
            missing={!placeValue(tender)}
            value={placeValue(tender) || "Not published by the agency"}
          />
        </dl>
      </Card>

      {/* Award notices carry the result — who won and for how much. The only
          place contract value exists anywhere in SAM.gov data. */}
      {tender.award && (
        <Card>
          <CardHeading
            icon={Trophy}
            title="Award"
            subtitle="The published result of this notice"
          />
          <div className="flex flex-wrap items-start gap-x-10 gap-y-4">
            {tender.award.amount && (
              <div className="font-outfit">
                <div className="text-[11px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-1">
                  Awarded amount
                </div>
                <div className="text-[24px] font-semibold text-[#18101C] leading-none">
                  {formatMoney(tender.award.amount)}
                </div>
              </div>
            )}
            <Meta
              label="Awarded to"
              missing={!tender.award.awardee}
              value={tender.award.awardee || "Not published"}
            />
            {tender.award.date && (
              <Meta label="Award date" value={formatDate(tender.award.date)} />
            )}
            {tender.award.number && (
              <Meta label="Award number" value={tender.award.number} />
            )}
          </div>
        </Card>
      )}

      <Card>
        <CardHeading icon={FileText} title="Description" subtitle="Published by the agency" />

        {tender.description ? (
          <>
            <div className="relative">
              <div
                ref={descriptionRef}
                style={
                  descriptionOverflows && !expanded
                    ? { maxHeight: DESCRIPTION_CLAMP_PX, overflow: "hidden" }
                    : undefined
                }
                className={`${DESCRIPTION_PROSE} max-w-[72ch] [&_table]:block [&_table]:overflow-x-auto`}
                // Sanitised server-side on the way in, against a fixed
                // allow-list, so nothing here is trusted from the wire.
                {...(descriptionIsHtml
                  ? { dangerouslySetInnerHTML: { __html: descriptionHtml } }
                  : {})}
              >
                {descriptionIsHtml ? undefined : (
                  <DescriptionText text={tender.description} />
                )}
              </div>

              {descriptionOverflows && !expanded && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-20
                             bg-gradient-to-t from-white via-white/85 to-transparent"
                />
              )}
            </div>

            {descriptionOverflows && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                className="mt-2 font-outfit text-[13px] font-medium text-[#BB42AE]
                           hover:text-[#A04A97] transition-colors"
              >
                {expanded ? "Show less" : "Show full description"}
              </button>
            )}
          </>
        ) : (
          <p className="font-outfit text-[14px] text-[#9CA3AF] italic">
            {tender.description_available
              ? "The full description has not been retrieved yet."
              : "The agency published no description for this tender."}
          </p>
        )}
      </Card>

      {/* Who to ask. Questions and clarifications go to these addresses, which
          makes this the most actionable block on the page. Renders only when
          the agency published someone — no empty theatre. */}
      {(tender.contacts?.length > 0 || tender.office_address) && (
        <Card>
          <CardHeading
            icon={Users}
            title="Contact"
            subtitle="Published by the agency for questions about this tender"
          />
          <div className="grid md:grid-cols-[1fr_auto] gap-x-12 gap-y-5">
            <div className="flex flex-col gap-5 min-w-0">
              {(tender.contacts || []).map((contact) => (
                <ContactPerson key={`${contact.role}-${contact.email || contact.name}`} contact={contact} />
              ))}
              {(!tender.contacts || tender.contacts.length === 0) && (
                <p className="font-outfit text-[13px] text-[#9CA3AF] italic">
                  No contact person published.
                </p>
              )}
            </div>

            {tender.office_address && (
              <div className="md:max-w-[260px] md:border-l md:border-[#F1F1F3] md:pl-8">
                <div className="font-outfit text-[11px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-1.5">
                  Contracting office
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="w-3.5 h-3.5 text-[#9CA3AF] shrink-0 mt-[3px]" />
                  <div className="font-outfit text-[13px] text-[#52525B] leading-relaxed">
                    {tender.office_address.name && (
                      <span className="block font-medium text-[#3F3F46]">
                        {tender.office_address.name}
                      </span>
                    )}
                    <span className="block">
                      {[tender.office_address.city, tender.office_address.state]
                        .filter(Boolean)
                        .join(", ")}
                      {tender.office_address.zip ? ` ${tender.office_address.zip}` : ""}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      <Card>
        <CardHeading
          icon={Download}
          title={
            <>
              Attachments
              {attachments.length > 0 && (
                <span className="ml-1 text-[11px] font-semibold bg-[#F8F3FF] text-[#BB42AE] rounded-full px-2 py-[2px]">
                  {attachments.length}
                </span>
              )}
            </>
          }
          subtitle={
            attachments.length > 0
              ? "Files published by the agency. Choose what to bring into your proposal project; you can add more later."
              : undefined
          }
          action={
            selectable.length > 0 && (
              <button
                type="button"
                onClick={() =>
                  setSelected(allSelected ? new Set() : new Set(selectable.map((d) => d.id)))
                }
                className="font-outfit text-[13px] font-medium border border-[#00000036] rounded-md
                           px-3 py-1.5 bg-white text-[#52525B] hover:bg-gray-50 transition-colors"
              >
                {allSelected ? "Clear all" : "Select all"}
              </button>
            )
          }
        />

        {attachments.length > 0 ? (
          attachments.map((doc) => {
            const on = selected.has(doc.id);
            const restricted = doc.access_restricted;
            return (
              <div
                key={doc.id}
                role="button"
                tabIndex={restricted ? -1 : 0}
                aria-disabled={restricted || undefined}
                onClick={() => !restricted && toggleDoc(doc.id)}
                onKeyDown={(e) =>
                  !restricted && (e.key === "Enter" || e.key === " ") && toggleDoc(doc.id)
                }
                className={`flex items-start gap-3 px-4 py-3.5 border rounded-xl mb-2 transition-colors ${
                  restricted
                    ? "border-[#E5E7EB] bg-[#FAFAFA] cursor-not-allowed opacity-75"
                    : on
                    ? "border-[#C95EBE] bg-[#F9F2F8] cursor-pointer"
                    : "border-[#E5E7EB] bg-white hover:border-[#C95EBE] cursor-pointer"
                }`}
              >
                <span
                  className={`w-[18px] h-[18px] rounded-[5px] border grid place-items-center shrink-0 mt-[1px] ${
                    restricted
                      ? "bg-[#F4F4F5] border-[#E4E4E7]"
                      : on
                      ? "bg-[#C95EBE] border-[#C95EBE]"
                      : "bg-white border-[#C9C9D1]"
                  }`}
                >
                  {restricted ? (
                    <Lock className="w-2.5 h-2.5 text-[#9CA3AF]" />
                  ) : (
                    on && <Check className="w-3 h-3 text-white" strokeWidth={3.5} />
                  )}
                </span>

                <div className="min-w-0 flex-1">
                  <div className="font-outfit text-[14px] font-medium text-[#18101C] leading-[1.35] break-words">
                    {doc.name}
                    {restricted && (
                      <span className="ml-2 text-[11px] text-[#9CA3AF] font-normal">
                        restricted, needs a SAM.gov login
                      </span>
                    )}
                    {/* SAM.gov lists attachment URLs without filenames, and
                        resolving each costs a request we cannot spend during a
                        sync. Saying so beats showing "Attachment 1" as if it
                        were the real name. */}
                    {doc.name_is_placeholder && (
                      <span className="ml-2 text-[11px] text-[#9CA3AF] font-normal">
                        name not published
                      </span>
                    )}
                  </div>
                  {(doc.file_type || doc.size_bytes) && (
                    <div className="flex items-center gap-2 font-outfit text-[12px] text-[#9CA3AF] mt-1">
                      {doc.file_type && (
                        <span className="text-[10px] font-semibold tracking-wide px-1.5 py-[1.5px]
                                         rounded bg-[#F4F4F5] text-[#71717A]">
                          {doc.file_type.toUpperCase()}
                        </span>
                      )}
                      {doc.size_bytes > 0 && <span>{formatBytes(doc.size_bytes)}</span>}
                    </div>
                  )}
                </div>

                {/* Lets someone look at the file before deciding whether to
                    import the whole tender — the checkbox above commits to a
                    project, this doesn't. Restricted docs have no bytes we
                    can serve, so no button rather than one that always fails. */}
                {!restricted && (
                  <button
                    type="button"
                    onClick={(e) => handleDownloadDoc(e, doc)}
                    disabled={downloadingIds.has(doc.id)}
                    title={`Download ${doc.name}`}
                    aria-label={`Download ${doc.name}`}
                    className="shrink-0 mt-[1px] p-1.5 rounded-md text-[#9CA3AF] hover:text-[#52525B]
                               hover:bg-[#F4F4F5] transition-colors disabled:opacity-50 disabled:cursor-wait"
                  >
                    {downloadingIds.has(doc.id) ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            );
          })
        ) : (
          // Three states, not two. Opening this page asks the source for the
          // list, so "unknown" here means the ask itself did not get through,
          // not that we never tried.
          <div className="border border-dashed border-[#D6D6DB] rounded-xl bg-[#FAFAFA] px-4 py-6 font-outfit text-[13px] text-[#71717A]">
            {unknownDocs ? (
              <>
                <b className="text-[#3F3F46] font-semibold">
                  We haven't been able to list this tender's attachments yet.
                </b>
                <span className="block mt-1">
                  Our nightly feed doesn't carry them, and {tender.source_label} hasn't
                  answered yet. Create Project checks again and brings in whatever it finds.
                </span>
              </>
            ) : links.length > 0 ? (
              <>
                <b className="text-[#3F3F46] font-semibold">
                  The agency published no files on this tender.
                </b>
                <span className="block mt-1">
                  Everything it attached is a web link. Those are listed below and stay
                  on {tender.source_label}, so nothing is downloaded into the project.
                </span>
              </>
            ) : (
              "The agency attached no documents to this tender."
            )}
          </div>
        )}
      </Card>

      {/* SAM.gov publishes attachments and links as two separate groups, and
          they behave differently: a link has no bytes behind it and never
          becomes a project document. Showing them in one list is what made a
          tender of four links read as a tender with four documents. */}
      {links.length > 0 && (
        <Card>
          <CardHeading
            icon={Link2}
            title={
              <>
                Links
                <span className="ml-1 text-[11px] font-semibold bg-[#EFF6FF] text-[#2563EB] rounded-full px-2 py-[2px]">
                  {links.length}
                </span>
              </>
            }
            subtitle={`Published as links rather than files. They open on the agency's site and are not brought into the project.`}
          />

          <ul className="flex flex-col gap-2">
            {links.map((link) => {
              const fileType = linkFileType(link.url);
              return (
                <li key={link.id}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-start gap-3 px-4 py-3 border border-[#E5E7EB] rounded-xl
                               bg-white hover:border-[#93C5FD] hover:bg-[#FAFCFF] transition-colors"
                  >
                    <span className="w-[18px] h-[18px] rounded-[5px] bg-[#EFF6FF] grid place-items-center shrink-0 mt-[1px]">
                      <Link2 className="w-3 h-3 text-[#2563EB]" />
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block font-outfit text-[14px] font-medium text-[#18101C]
                                       leading-[1.35] break-words group-hover:text-[#1D4ED8] transition-colors">
                        {link.name}
                      </span>
                      <span className="flex items-center gap-2 mt-1">
                        {fileType && (
                          <span className="text-[10px] font-semibold tracking-wide px-1.5 py-[1.5px]
                                           rounded bg-[#F4F4F5] text-[#71717A]">
                            {fileType.toUpperCase()}
                          </span>
                        )}
                        <span className="font-outfit text-[12px] text-[#9CA3AF] truncate">
                          {hostOf(link.url)}
                        </span>
                      </span>
                    </span>

                    <ExternalLink className="w-3.5 h-3.5 text-[#C4C4CC] shrink-0 mt-1
                                             group-hover:text-[#2563EB] transition-colors" />
                  </a>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {importError && <Callout tone="danger">{importError}</Callout>}

      {pendingWarning && (
        <ConfirmDialog
          {...warningDialog(pendingWarning, tender, attachments, links)}
          cancelLabel="Cancel"
          busy={importing}
          onConfirm={startImport}
          onCancel={() => setPendingWarning(null)}
        />
      )}

    </DetailShell>
  );
}
